import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardTile from './DashboardTile';
import CubeRotator from './CubeRotator';
import type { Mission, LastAchievement, StreakData } from '@/types/dashboard';

interface MissionsCubeProps {
  mission: Mission | null;
  achievement: LastAchievement | null;
  streak: StreakData;
}

const MissionFace: FC<{ mission: Mission | null; onClick: () => void }> = ({ mission, onClick }) => (
  <div
    className="w-full h-full flex flex-col justify-between p-1 cursor-pointer"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
  >
    <div>
      <p className="text-2xs font-bold uppercase tracking-[0.1em] mb-1" style={{ color: 'var(--tile-text-muted)' }}>
        Próxima Missão
      </p>
      {mission ? (
        <>
          <p className="text-sm font-bold leading-tight line-clamp-2 mb-2" style={{ color: 'var(--tile-text-primary)' }}>
            {mission.title}
          </p>
          <p className="text-2xs line-clamp-1" style={{ color: 'var(--tile-text-secondary)' }}>
            {mission.description}
          </p>
        </>
      ) : (
        <div className="text-center px-1 py-2">
          <span className="material-symbols-outlined text-3xl mb-1 block" style={{ color: 'var(--tile-text-muted)' }}>
            flag
          </span>
          <p className="text-sm font-bold mb-1" style={{ color: 'var(--tile-text-primary)' }}>
            Sem desafios agora
          </p>
          <p className="text-2xs leading-snug" style={{ color: 'var(--tile-text-muted)' }}>
            Continue suas trilhas pra acumular XP. Desafios especiais aparecem aqui de vez em quando!
          </p>
        </div>
      )}
    </div>
    {mission && (
      <div>
        <div className="w-full h-1.5 rounded-full" style={{ background: 'var(--tile-bar-bg)' }}>
          <div
            className="h-full rounded-full"
            style={{ width: `${mission.progress}%`, background: 'var(--tile-accent)' }}
          />
        </div>
        <span className="text-2xs font-medium mt-0.5 block" style={{ color: 'var(--tile-accent)' }}>
          {mission.progress}%
        </span>
      </div>
    )}
  </div>
);

const AchievementFace: FC<{ achievement: LastAchievement | null; onClick: () => void }> = ({ achievement, onClick }) => (
  <div
    className="w-full h-full flex flex-col justify-between p-1 cursor-pointer"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
  >
    <div>
      <p className="text-2xs font-bold uppercase tracking-[0.1em] mb-1" style={{ color: 'var(--tile-text-muted)' }}>
        Última Conquista
      </p>
      {achievement ? (
        <>
          <div className="flex items-center gap-2 mb-1">
            <span className="material-symbols-outlined text-lg" style={{ color: 'var(--tile-accent)' }}>
              {achievement.icon || 'emoji_events'}
            </span>
            <p className="text-sm font-bold leading-tight" style={{ color: 'var(--tile-text-primary)' }}>
              {achievement.title}
            </p>
          </div>
          <p className="text-2xs line-clamp-2" style={{ color: 'var(--tile-text-secondary)' }}>
            {achievement.description}
          </p>
        </>
      ) : (
        <p className="text-xs" style={{ color: 'var(--tile-text-muted)' }}>Complete cursos para ganhar conquistas</p>
      )}
    </div>
    {achievement?.earned_at && (
      <span className="text-2xs" style={{ color: 'var(--tile-text-muted)' }}>
        {new Date(achievement.earned_at).toLocaleDateString('pt-BR')}
      </span>
    )}
  </div>
);

const StreakFace: FC<{ streak: StreakData; onClick: () => void }> = ({ streak, onClick }) => (
  <div
    className="w-full h-full flex flex-col items-center justify-center p-1 cursor-pointer"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
  >
    <p className="text-2xs font-bold uppercase tracking-[0.1em] mb-2" style={{ color: 'var(--tile-text-muted)' }}>
      Streak Ativo
    </p>
    <span className="text-3xl mb-1">🔥</span>
    <span className="text-2xl font-black" style={{ color: 'var(--tile-text-primary)' }}>
      {streak.current} {streak.current === 1 ? 'dia' : 'dias'}
    </span>
    <span className="text-2xs mt-1" style={{ color: 'var(--tile-text-muted)' }}>
      Recorde: {streak.best} dias
    </span>
  </div>
);

const MissionsCube: FC<MissionsCubeProps> = ({ mission, achievement, streak }) => {
  const navigate = useNavigate();

  const faces = [
    <MissionFace key="mission" mission={mission} onClick={() => navigate('/trilhas')} />,
    <AchievementFace key="achievement" achievement={achievement} onClick={() => navigate('/perfil')} />,
    <StreakFace key="streak" streak={streak} onClick={() => navigate('/perfil')} />,
  ];

  return (
    <DashboardTile data-testid="missions-tile">
      <CubeRotator faces={faces} interval={7000} height={150} />
    </DashboardTile>
  );
};

export default MissionsCube;
